/**
 * Typed client for /components/* — the ComponentManifest catalog: one row per
 * worker binary per platform/arch (cpu-miner, gpu-miner, inference, …) with
 * its URL, hash, version, gating (gpuRequirement, minVramMb) and config.
 *
 * All endpoints envelope ({ success, data }) via handleSuccess → unwrap().
 * Live per-component session counts come from /admin-infra/components
 * (raw, see adminInfra.ts) and are joined client-side by _id.
 */
import { client, unwrap } from './client';

export const COMPONENT_TYPES = [
  'cpu-miner',
  'gpu-miner',
  'bandwidth-relay',
  'cache-edge',
  'storage-node',
  'inference',
  'shell'
] as const;
export const PLATFORMS = ['windows', 'mac', 'linux'] as const;
export const ARCHS = ['x86_64', 'arm64'] as const;
export const GPU_REQUIREMENTS = ['', 'nvidia', 'amd', 'nvidia|amd'] as const;
export const CONFIG_FORMATS = [
  '',
  'xmrig',
  'lolminer',
  'bzminer',
  'gminer',
  'trex',
  'srbminer',
  'kawpowminer',
  'bandwidth',
  'storj'
] as const;

export interface ComponentManifest {
  _id: string;
  name: string;
  type: string;
  version: string;
  platform: string;
  arch: string;
  binaryUrl: string;
  binaryHash: string;
  binarySize: number;
  binaryName: string;
  archive: boolean;
  entrypoint: string;
  algorithm: string;
  configFormat: string;
  enabled: boolean;
  gpuRequirement: string;
  minVramMb: number;
  minShellVersion: string;
  config: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

/** Fields an admin edits on the screen (everything else stays server-side). */
export type ComponentEdit = Partial<
  Pick<
    ComponentManifest,
    | 'name'
    | 'version'
    | 'binaryUrl'
    | 'binaryHash'
    | 'binarySize'
    | 'binaryName'
    | 'archive'
    | 'entrypoint'
    | 'algorithm'
    | 'configFormat'
    | 'enabled'
    | 'gpuRequirement'
    | 'minVramMb'
    | 'minShellVersion'
    | 'config'
  >
>;

export interface ComponentCreate extends ComponentEdit {
  type: string;
  platform: string;
  arch: string;
  binaryUrl: string;
}

export async function fetchComponents(): Promise<ComponentManifest[]> {
  const r = await unwrap<{ docs: ComponentManifest[] }>(client.get('/components'));
  return r.docs;
}

export function createComponent(body: ComponentCreate): Promise<ComponentManifest> {
  return unwrap(client.post('/components', body));
}

export function updateComponent(
  id: string,
  body: ComponentEdit
): Promise<ComponentManifest> {
  return unwrap(client.patch(`/components/${id}`, body));
}

export async function deleteComponent(id: string): Promise<void> {
  await client.delete(`/components/${id}`);
}
